/**
 * NATS authenticator factory · spec 05 §L1494-L1530.
 *
 * The nats-core authenticator callback shape (verified empirically at
 * `/tmp/morfi-sdk-spec-review/claim*` against `@nats-io/nats-core@3.4.0`)
 * is `(nonce?: string) => { jwt: string }`. The callback fires on every
 * reconnect attempt — i.e. it captures the JWT closure-style, never
 * dereferenced at construction time.
 *
 * This wrapper takes a `currentJwt: () => string` (typically the
 * `read` method of a `JwtStore`) and produces the authenticator the
 * nats client expects. Because the closure reads the cell on every
 * call, mid-session refresh can simply `jwtStore.update(fresh)` and
 * the next `nc.reconnect()` picks up the new credential without
 * reconstructing the client.
 *
 * Anon → user transition: when the JWT is empty string the broker
 * accepts an anon SUB scope; once a user signs in, `jwtStore.update`
 * swaps in the user JWT and the next reconnect re-attempts every
 * pending-auth subject (handled in the routing-table reconcile).
 */

export type JwtAuthenticator = (nonce?: string) => { jwt: string };

/**
 * Build a `nats-core` authenticator from a JWT-reader closure.
 * The closure MUST be side-effect-free — nats-core may call it multiple
 * times in a single CONNECT handshake (nonce challenge + ACK).
 */
export function jwtAuthenticator(currentJwt: () => string): JwtAuthenticator {
  return (_nonce?: string) => ({ jwt: currentJwt() });
}

// ─────────────────────────────────────────────────────────────────────────
// JWT-mint response shape · T301 (mint client) + T6009 (wire-sub effect)
// ─────────────────────────────────────────────────────────────────────────
//
// `JwtResponse` is the contract between the JWT-mint client (T301) and the
// wire-subscription effect (T6009 · `useNatsSubscription`). Both pieces
// agree on:
//
//   - `jwt`: the bearer token the WS client sends in CONNECT,
//   - `allow_sub`: the server-side authorized subject roots — the SDK opens
//     one wire subscription per generated entry,
//   - `expires_at`: epoch-ms expiry used by the refresh ladder (T6013).
//
// T301 owns the mint client (HTTP call + retry ladder + Better Stack
// telemetry). This declaration lives here so T6009 can be implemented and
// unit-tested without depending on the mint client.

export interface JwtResponse {
  /**
   * The minted NATS JWT (already base64-encoded by the backend). The SDK
   * forwards this via the authenticator-callback closure on every
   * reconnect — the closure reads `JwtStore.read()` each time, so
   * mid-session refresh works without reconstructing the client.
   */
  readonly jwt: string;
  /**
   * Server-authorized subject roots. The SDK opens exactly one wire
   * subscription per entry. Anon → 5 wildcards. Authed → 5 wildcards + 2
   * user-leaf subjects. The SDK MUST NOT add to or filter this list —
   * Synadia enforces it server-side and a SUB outside `allow_sub` errors
   * with `ERR_PERMISSIONS_VIOLATION`.
   */
  readonly allow_sub: readonly string[];
  /**
   * Epoch-ms timestamp at which the JWT expires. The refresh ladder
   * (T6013) schedules a re-mint relative to this value.
   */
  readonly expires_at: number;
}

/**
 * Type guard for `JwtResponse`. Used by the mint-client response parser
 * and by tests that build fixture responses.
 */
export function isJwtResponse(value: unknown): value is JwtResponse {
  if (value === null || typeof value !== 'object') return false;
  const r = value as Record<string, unknown>;
  if (typeof r.jwt !== 'string' || r.jwt.length === 0) return false;
  if (!Array.isArray(r.allow_sub)) return false;
  for (const entry of r.allow_sub) {
    if (typeof entry !== 'string') return false;
  }
  if (typeof r.expires_at !== 'number' || !Number.isFinite(r.expires_at)) {
    return false;
  }
  return true;
}

// ─────────────────────────────────────────────────────────────────────────
// NATS-mint HTTP client · T2006 + T301 · file 05 §L593-622, §L1311.
// ─────────────────────────────────────────────────────────────────────────
//
// Two shapes coexist in this file. They share the same endpoint and the
// same return type but differ in retry semantics + auth header:
//
//   - `mintNatsCredentials` (T2006) — session-cookie based. Backend
//     reads the session cookie (if any) and returns either anon (public
//     wildcards only) or user (adds the two per-user leaves) credentials.
//     Single attempt; surface 401/403 directly. Used by browser sessions.
//
//   - `mintCredentials` (T301) — bearer-token based + retry ladder.
//     Request omits the auth header for anon callers (backend identifies
//     them by IP + rate-limits per-IP); passes a bearer token for authed
//     callers. Retries 5xx 3 times at 30/60/120 s before surfacing
//     DEGRADED_NO_NATS(cause:'mint-failed'). 4xx is terminal.
//
// Both return the same `MintedCredentials` shape (`jwt`, `expiresAt`,
// `kind`); the second function pre-dates the first but the two paths
// stayed because session-cookie flows (web) and bearer-token flows
// (native) have different rate-limit + retry profiles.
//
// ── ADR-003 (T19200) — canonical owner ─────────────────────────────────
// `mintCredentials` (T301) is the CANONICAL mint client. New call sites
// MUST import `mintCredentials`; `mintNatsCredentials` is retained as a
// deprecated session-cookie shim for the brief migration window. See
// `specs/009-data-fetching-audit/_research/adr-003-mint-client-canonical-owner.md`
// for the full rationale (retry ladder, terminal-error routing,
// telemetry centralisation, spec wire-shape match).

import { newCorrelationId } from '../../errors/correlation.js';
import { makeSdkError } from '../../errors/SdkError.js';

export type MintKind = 'anon' | 'user';

export interface MintedCredentials {
  readonly jwt: string;
  /** Epoch seconds. */
  readonly expiresAt: number;
  readonly kind: MintKind;
  /**
   * Server-authorized subscribe roots — 5 wildcards (anon) or those 5 +
   * 2 per-user leaf subjects (user). The SDK opens exactly one wire
   * subscription per entry. Optional for back-compat with mint mocks
   * that predate the field — absent ⇒ the caller falls back to the anon
   * scope.
   */
  readonly allow_sub?: readonly string[];
}

// ─────────────────────────────────────────────────────────────────────────
// T2006 · session-cookie mint (no retry ladder; single attempt)
// ─────────────────────────────────────────────────────────────────────────

export interface MintOpts {
  readonly baseUrl: string;
  readonly fetchImpl?: typeof fetch;
  readonly signal?: AbortSignal;
  /** Optional ULID override (used by tests to assert correlationId routing). */
  readonly correlationId?: string;
}

/**
 * Session-optional mint. Backend reads the session cookie (if any) and
 * returns either anon (public wildcards only) or user (adds the two
 * per-user leaves) credentials. There is no second endpoint and no
 * second URL — the same path serves both callers.
 *
 * Throws `SdkError`:
 *   - category 'network' code 'FETCH_FAILED' on fetch reject.
 *   - category 'auth' code 'UNAUTHORIZED' on 401/403.
 *   - category 'server' code 'INTERNAL' on other non-2xx.
 *   - category 'schema' code 'SHAPE_MISMATCH' on malformed body.
 */
export async function mintNatsCredentials(opts: MintOpts): Promise<MintedCredentials> {
  const fetchImpl =
    opts.fetchImpl ??
    (globalThis as { fetch?: typeof fetch }).fetch ??
    (((): never => {
      throw new Error(
        'mintNatsCredentials: no fetch implementation available — pass opts.fetchImpl',
      );
    }) as unknown as typeof fetch);
  const corr = opts.correlationId ?? newCorrelationId();
  let res: Response;
  try {
    const init: RequestInit = {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-correlation-id': corr },
    };
    if (opts.signal !== undefined) init.signal = opts.signal;
    res = await fetchImpl(`${opts.baseUrl}/v1/auth/nats-credentials`, init);
  } catch (cause) {
    throw makeSdkError({
      category: 'network',
      code: 'FETCH_FAILED',
      cause,
      correlationId: corr,
      userMessage: 'errors.network.mint_failed',
      devMessage: 'mintNatsCredentials: network failure',
    });
  }
  if (res.status === 401 || res.status === 403) {
    throw makeSdkError({
      category: 'auth',
      code: 'UNAUTHORIZED',
      httpStatus: res.status as 401 | 403,
      correlationId: corr,
      userMessage: 'errors.auth.mint_rejected',
      devMessage: `mintNatsCredentials: ${res.status}`,
    });
  }
  if (!res.ok) {
    throw makeSdkError({
      category: 'server',
      code: 'INTERNAL',
      httpStatus: res.status,
      correlationId: corr,
      userMessage: 'errors.server.mint',
      devMessage: `mintNatsCredentials: ${res.status}`,
    });
  }
  const body = (await res.json()) as unknown;
  if (!isMintedCredentials(body)) {
    throw makeSdkError({
      category: 'schema',
      code: 'SHAPE_MISMATCH',
      correlationId: corr,
      userMessage: 'errors.schema.mint',
      devMessage: 'mintNatsCredentials: malformed response',
    });
  }
  return body;
}

function isMintedCredentials(v: unknown): v is MintedCredentials {
  if (typeof v !== 'object' || v === null) return false;
  const r = v as Record<string, unknown>;
  if (typeof r.jwt !== 'string' || r.jwt.length === 0) return false;
  if (typeof r.expiresAt !== 'number' || !Number.isFinite(r.expiresAt)) {
    return false;
  }
  if (r.kind !== 'anon' && r.kind !== 'user') return false;
  if (r.allow_sub !== undefined) {
    if (!Array.isArray(r.allow_sub)) return false;
    for (const s of r.allow_sub) {
      if (typeof s !== 'string') return false;
    }
  }
  return true;
}

// ─────────────────────────────────────────────────────────────────────────
// T301 · bearer-token mint with 30/60/120 s retry ladder
// ─────────────────────────────────────────────────────────────────────────

export interface MintCredentialsInput {
  /** Bearer token for the backend session — null for anon mint. */
  readonly bearerToken: string | null;
  /** SDK passes a long-lived signal that aborts on tab close. */
  readonly signal?: AbortSignal;
}

/** Default bound on a single mint request; a hung fetch counts as a retryable failure. */
export const MINT_REQUEST_TIMEOUT_MS = 10_000;

export interface MintClientConfig {
  /** Absolute URL to the API host, e.g. https://api.morfi.markets */
  readonly baseUrl: string;
  /** Retry delays in ms between attempts (SDKProvider passes [1_000, 2_000, 4_000]). */
  readonly retryDelaysMs: readonly [number, number, number];
  /** Per-attempt request timeout in ms. Defaults to `MINT_REQUEST_TIMEOUT_MS`. */
  readonly requestTimeoutMs?: number;
  /** Injected fetch (defaults to host `globalThis.fetch`). */
  readonly fetchImpl?: typeof fetch;
  /** Injected ULID provider (defaults to `newCorrelationId`). */
  readonly idempotencyKey?: () => string;
  /** Injected `setTimeout` for deterministic tests. */
  readonly setTimeoutFn?: (handler: () => void, ms: number) => unknown;
  /** Injected `clearTimeout`. */
  readonly clearTimeoutFn?: (handle: unknown) => void;
}

/**
 * Mint (or refresh) a NATS JWT via `POST /v1/auth/nats-credentials`.
 *
 * Retry semantics:
 *   - 5xx — retried 3 times at the configured ladder. After the 3rd failure,
 *     throws `SdkError({ category:'realtime-degraded', code:'NATS_MINT_FAILED' })`;
 *     the caller's cold-connect ladder keeps re-minting from there.
 *   - 4xx — terminal. Throws `SdkError({ category:'auth', code:'MINT_CLIENT_ERROR' })`
 *     immediately. No retry — bad client.
 *   - Network failure (fetch reject) — counted as 5xx for retry purposes.
 *   - A request that does not answer within `requestTimeoutMs` is aborted and
 *     counted as a network failure, so a hung endpoint cannot hang the connect.
 *
 * SSR-safe: this module lives under the `/client` barrel and is never
 * imported from a Server Component.
 */
export async function mintCredentials(
  config: MintClientConfig,
  input: MintCredentialsInput,
): Promise<MintedCredentials> {
  const fetchImpl =
    config.fetchImpl ??
    (globalThis as { fetch?: typeof fetch }).fetch ??
    (((): never => {
      throw new Error('mintCredentials: no fetch implementation available — pass config.fetchImpl');
    }) as unknown as typeof fetch);
  const idempotencyKey = config.idempotencyKey ?? newCorrelationId;
  const setTimeoutFn =
    config.setTimeoutFn ??
    ((h: () => void, ms: number): unknown =>
      (globalThis as { setTimeout: typeof setTimeout }).setTimeout(h, ms));
  const clearTimeoutFn =
    config.clearTimeoutFn ??
    ((h: unknown): void =>
      (globalThis as { clearTimeout: typeof clearTimeout }).clearTimeout(
        h as ReturnType<typeof setTimeout>,
      ));

  const requestTimeoutMs = config.requestTimeoutMs ?? MINT_REQUEST_TIMEOUT_MS;
  const url = `${config.baseUrl}/v1/auth/nats-credentials`;
  const corr = idempotencyKey();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-correlation-id': corr,
  };
  if (input.bearerToken !== null) {
    headers.Authorization = `Bearer ${input.bearerToken}`;
  }

  const maxAttempts = config.retryDelaysMs.length + 1;
  let lastErr: unknown = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (input.signal?.aborted === true) {
      throw makeSdkError({
        category: 'realtime-degraded',
        code: 'NATS_MINT_ABORTED',
        correlationId: corr,
        userMessage: 'errors.realtime.mint_failed',
        devMessage: 'mintCredentials: aborted before attempt',
        cause: lastErr,
      });
    }
    // Per-attempt bound: a fetch that never answers must fail this attempt,
    // not the whole connect. The external signal (tab close) aborts too.
    const attemptController = new AbortController();
    const onExternalAbort = (): void => attemptController.abort();
    input.signal?.addEventListener('abort', onExternalAbort, { once: true });
    const attemptTimer = setTimeoutFn(() => attemptController.abort(), requestTimeoutMs);
    try {
      const init: RequestInit = {
        method: 'POST',
        headers,
        // Web: the Morfi session is an httpOnly `morfi_session` cookie —
        // `credentials:'include'` lets it ride the mint so the backend
        // returns a USER credential (7 roots) for a signed-in browser.
        // RN: no cookies — identity rides the `Authorization: Bearer`
        // header set above; `include` is a harmless no-op there.
        credentials: 'include',
        signal: attemptController.signal,
      };
      const res = await fetchImpl(url, init);
      if (res.status >= 200 && res.status < 300) {
        const body = (await res.json()) as unknown;
        if (!isMintedCredentials(body)) {
          throw makeSdkError({
            category: 'realtime-degraded',
            code: 'NATS_MINT_MALFORMED',
            correlationId: corr,
            userMessage: 'errors.realtime.mint_malformed',
            devMessage: 'mintCredentials: malformed response',
          });
        }
        return body;
      }
      if (res.status >= 400 && res.status < 500) {
        // 4xx terminal — surface immediately, no retry.
        throw makeSdkError({
          category: 'auth',
          code: 'MINT_CLIENT_ERROR',
          httpStatus: res.status as 401 | 403,
          correlationId: corr,
          userMessage: 'errors.auth.mint_client_error',
          devMessage: `mintCredentials: ${res.status}`,
        });
      }
      // 5xx — capture and retry.
      lastErr = makeSdkError({
        category: 'server',
        code: 'MINT_SERVER_ERROR',
        httpStatus: res.status,
        correlationId: corr,
        userMessage: 'errors.server.mint',
        devMessage: `mintCredentials: ${res.status}`,
      });
    } catch (err) {
      // Re-throw 4xx terminal errors immediately.
      const sdkErr = err as { category?: string } | null;
      if (sdkErr?.category === 'auth') throw err;
      lastErr = err;
    } finally {
      clearTimeoutFn(attemptTimer);
      input.signal?.removeEventListener('abort', onExternalAbort);
    }

    if (attempt < config.retryDelaysMs.length) {
      const delay = config.retryDelaysMs[attempt] ?? 0;
      await new Promise<void>((resolve) => {
        const handle = setTimeoutFn(() => resolve(), delay);
        if (input.signal !== undefined) {
          input.signal.addEventListener(
            'abort',
            () => {
              clearTimeoutFn(handle);
              resolve();
            },
            { once: true },
          );
        }
      });
    }
  }

  // All retries exhausted — surface realtime-degraded so the connectivity
  // machine can flip to terminal DEGRADED_NO_NATS(cause:'mint-failed').
  throw makeSdkError({
    category: 'realtime-degraded',
    code: 'NATS_MINT_FAILED',
    correlationId: corr,
    userMessage: 'errors.realtime.mint_failed',
    devMessage: `mintCredentials: exhausted ${maxAttempts} attempts`,
    cause: lastErr,
  });
}
